import React from 'react';
import PageHero from './PageHero';
import { Award, Users, Flag, HeartHandshake } from 'lucide-react';
import studentsImg from '../assets/Banner-GMIS Students.png';
import seniorImg from '../assets/SENIOR SCHOOL.png';

export default function StudentLeadershipPage({ setPage }) {
  return (
    <div className="w-full bg-[#f8f9fa] min-h-screen text-ink">
      {/* ── Hero ─────────────────────────────────────────────────────────────── */}
      <PageHero
        title="Student Leadership"
        eyebrow="School Life at GMIS Jakarta"
        subtitle="Empowering Students to Lead"
        breadcrumbs={["Home", "School Life", "Student Leadership"]}
        bgImage={seniorImg}
      />

      {/* ── Main Content Container ────────────────────────────────────────── */}
      <div className="max-w-[1200px] mx-auto px-6 lg:px-[80px] py-16 lg:py-24 box-border">

        {/* ── Section 1: Intro & House System ── */}
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16 mb-20 lg:mb-28">
          <div className="w-full lg:w-5/12">
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-24 h-24 bg-gold/15 rounded-2xl -z-10" />
              <img
                src={seniorImg}
                alt="Student Leadership at GMIS Jakarta"
                className="relative z-10 w-full h-[360px] md:h-[420px] rounded-2xl shadow-xl object-cover border-4 border-white"
              />
              <div className="absolute -bottom-4 -right-4 w-20 h-20 bg-navy/10 rounded-2xl -z-10" />
            </div>
          </div>

          <div className="w-full lg:w-7/12 flex flex-col gap-6 text-left">
            <div>
              <div className="inline-flex items-center gap-2 mb-3 px-3.5 py-1 rounded-full border border-gold/30 bg-gold/10">
                <span className="text-gold text-[11px] font-bold tracking-[0.2em] uppercase">School Life</span>
              </div>
              <h2 className="m-0 font-heading font-bold text-3xl md:text-4xl text-navy tracking-tight">
                Empowering Students to Lead
              </h2>
              <div className="w-16 h-1 bg-gold mt-4" />
            </div>

            <p className="text-[17px] md:text-[19px] font-medium text-navy leading-relaxed">
              Leadership is nurtured through meaningful opportunities to participate, collaborate and take responsibility throughout the school journey.
            </p>

            <div className="p-6 rounded-2xl bg-white border border-line shadow-sm">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-gold/15 text-gold-ink flex items-center justify-center shrink-0 mt-0.5">
                  <Flag className="w-5 h-5 text-gold-ink" />
                </div>
                <p className="text-[16px] text-ink/80 leading-relaxed m-0">
                  Through the House System, students work together across sports, quizzes, debates, arts and community activities, developing collaboration, discipline, confidence and school spirit.
                </p>
              </div>
            </div>

            <div className="p-6 rounded-2xl bg-white border border-line shadow-sm">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-navy/10 text-navy flex items-center justify-center shrink-0 mt-0.5">
                  <Users className="w-5 h-5 text-navy" />
                </div>
                <p className="text-[16px] text-ink/80 leading-relaxed m-0">
                  Students also have opportunities to take on leadership roles within their respective programmes. Head Boys and Head Girls represent and support their student communities, taking on responsibilities that encourage them to lead by example, communicate with others and contribute to school life.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ── Divider ── */}
        <div className="w-full h-px bg-line/80 my-16 lg:my-20" />

        {/* ── Section 2: Investiture & Purpose of Leadership ── */}
        <div className="flex flex-col lg:flex-row-reverse items-center gap-10 lg:gap-16 mb-20 lg:mb-28">
          <div className="w-full lg:w-5/12">
            <div className="relative">
              <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-navy/10 rounded-2xl -z-10" />
              <img
                src={studentsImg}
                alt="Student Leaders and Investiture"
                className="relative z-10 w-full h-[360px] md:h-[420px] rounded-2xl shadow-xl object-cover border-4 border-white"
              />
              <div className="absolute -top-4 -left-4 w-20 h-20 bg-gold/15 rounded-2xl -z-10" />
            </div>
          </div>

          <div className="w-full lg:w-7/12 flex flex-col gap-6 text-left">
            <div>
              <div className="inline-flex items-center gap-2 mb-3 px-3.5 py-1 rounded-full border border-gold/30 bg-gold/10">
                <span className="text-gold text-[11px] font-bold tracking-[0.2em] uppercase">Recognition & Responsibility</span>
              </div>
              <h2 className="m-0 font-heading font-bold text-3xl md:text-4xl text-navy tracking-tight">
                The Leadership Journey
              </h2>
              <div className="w-16 h-1 bg-gold mt-4" />
            </div>

            <div className="p-6 rounded-2xl bg-white border border-line shadow-sm">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-gold/15 text-gold-ink flex items-center justify-center shrink-0 mt-0.5">
                  <Award className="w-5 h-5 text-gold-ink" />
                </div>
                <p className="text-[16px] text-ink/80 leading-relaxed m-0">
                  The Investiture Ceremony formally recognises these student leaders as they take on their responsibilities, marking an important moment in their leadership journey.
                </p>
              </div>
            </div>

            <div className="p-6 rounded-2xl bg-white border border-line shadow-sm">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-navy/10 text-navy flex items-center justify-center shrink-0 mt-0.5">
                  <HeartHandshake className="w-5 h-5 text-navy" />
                </div>
                <p className="text-[16px] text-ink/80 leading-relaxed m-0">
                  From leading by example to working with their peers, students learn that leadership is not simply about having a title. It is about taking responsibility, listening to others and making a positive contribution to the school community.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ── Section 3: Concluding Motto Banner ── */}
        <div className="w-full bg-navy rounded-3xl p-10 md:p-14 text-center text-white shadow-xl relative overflow-hidden">
          <div className="w-16 h-1 bg-gold mx-auto mb-6" />
          <h3 className="font-heading font-bold text-2xl sm:text-3xl md:text-4xl leading-snug max-w-3xl mx-auto tracking-tight">
            “Building confidence. Taking responsibility. Leading with purpose.”
          </h3>
          <div className="mt-8 flex justify-center">
            <button
              onClick={() => {
                if (setPage) setPage('contact');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="px-8 py-3.5 rounded-lg bg-gold text-navy font-heading font-bold text-xs uppercase tracking-widest hover:bg-gold/90 transition-all shadow-md"
            >
              Contact Us
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
